import React, { useEffect, useState, useContext } from "react";
import Link from "gatsby-link";
import { storyblokEditable } from "@storyblok/react";
import Logo from "src/images/logo.svg";
import LogoCondensed from "src/images/logo_condensed.svg";
import MenuOpen from "src/images/menu_open.svg";
import MenuClose from "src/images/menu_close.svg";
import MenuHover from "src/images/plus-rounded.svg";
import resolveLink from "src/utils/resolveLink";
import { PageContext } from "utils/context";

const Header = ({ settings }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [animate, setAnimate] = useState(true);
  const context = useContext(PageContext);
  const fixedHeader = context?.story?.content?.body
    ? ["work-header", "article-header"].includes(
        context?.story?.content?.body[0]?.component
      )
    : false;
  const [isShown, setIsShown] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  // Change the style of the navbar on scroll
  const handleScroll = () => {
    if (window.scrollY > 50) {
      setScrolled(true);
      setAnimate(true);
    } else {
      setScrolled(false);
      setAnimate(false);
    }
  };

  useEffect(() => {
    // Initial onload animation
    setTimeout(() => {
      if (!scrolled) {
        setAnimate(false);
      }
    }, 500);
    window.addEventListener("scroll", handleScroll);
    // Body overflow control
    //open ? document.body.style.overflow = "hidden" : document.body.style.overflow = "visible"
    // Gets screen size - to fix mobile viewport height problem
    if (typeof window !== "undefined") {
      function handleResize() {
        let vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
      }
      window.addEventListener("resize", handleResize);
      handleResize();
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [scrolled, open]);

  return (
    <div
      {...storyblokEditable(settings)}
      key={settings._uid}
      className={`sticky top-0 z-50 w-full ${fixedHeader ? "!fixed" : ""}`}
    >
      <div className="max-w-screen mx-auto px-6 lg:px-16">
        <div
          className={`flex w-full items-center justify-between space-x-10 py-6 transition-all duration-300 lg:py-8`}
        >
          <div className="z-[9999991] flex items-center justify-start">
            <Link to="/" className="flex flex-row items-center justify-center">
              <Logo
                className={`${
                  scrolled ? "opacity-0" : "opacity-100"
                } duration-300 ${
                  !scrolled && fixedHeader && !open
                    ? "text-[#fff]"
                    : "text-primary"
                }`}
              />
              <LogoCondensed
                className={`absolute ${
                  scrolled ? "opacity-100" : "opacity-0"
                } duration-300 ${
                  !scrolled && fixedHeader && !open
                    ? "text-[#fff]"
                    : "text-primary"
                }`}
              />
            </Link>
          </div>
          <div className="flex h-[35px] flex-row items-center justify-center">
            <div
              className="group z-[999999] -mt-1 outline-none focus:outline-none"
              role="button"
              tabIndex="0"
              onClick={() => handleClick()}
              onKeyDown={() => handleClick()}
              onMouseEnter={() => setIsShown(true)}
              onMouseLeave={() => setIsShown(false)}
            >
              <div
                class={`relative flex h-9 w-9 items-center justify-center rounded-full bg-[#F9DFE8] transition duration-300  `}
              >
                <div
                  class={`absolute top-1/2 left-1/2 h-[18px] w-[3px] -translate-y-1/2 -translate-x-1/2 bg-[#091DF3] transition duration-300 md:w-[3px]  ${
                    !isShown && !open ? "opacity-0" : "opacity-100"
                  } ${open && "rotate-90"}`}
                ></div>
                <div
                  class={`absolute top-1/2 left-1/2 h-[18px] w-[3px] -translate-y-1/2 -translate-x-1/2 rotate-90 bg-[#091DF3] transition duration-300 md:w-[3px] ${
                    !isShown && !open ? "opacity-0" : "opacity-100"
                  }`}
                  $
                ></div>
                <MenuOpen
                  width="35"
                  className={`transition duration-500 ${
                    isShown && "rotate-180 opacity-0"
                  } ${open && "opacity-0 "}`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* The overlay menu */}
      <div class={`${open ? "visible" : "invisible"}`}>
        <div
          static
          className={`absolute inset-0 z-[99999] h-screen overflow-hidden duration-300 ease-in-out ${
            open ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="bg-secondary h-screen">
            <div className={`flex h-full flex-col items-center justify-center`}>
              <div className="w-full overflow-x-hidden">
                <nav className="mx-auto grid gap-y-3 px-6 py-6 lg:gap-y-4 lg:px-16">
                  {settings.header_navigation.map((item, index) => (
                    <div
                      {...storyblokEditable(item)}
                      key={item._uid}
                      className="transition duration-300 hover:translate-x-5"
                    >
                      <Link
                        to={
                          item.target.cached_url === "home"
                            ? "/"
                            : resolveLink(item.target.cached_url)
                        }
                        className={`group font-poppins text-primary font-display py-1 text-5xl leading-none tracking-[-2px] transition ease-in-out md:text-7xl lg:text-8xl ${
                          open
                            ? "translate-y-0 opacity-100 duration-500"
                            : "translate-y-2 opacity-0"
                        }`}
                        style={
                          open ? { transitionDelay: `${index * 0.2}s` } : null
                        }
                      >
                        <span>{item.name}</span>
                      </Link>
                    </div>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
